export const BILLING_DETAILS_ATTRIBUTE = 'data-billing-details';

const HEX_JSON_ESCAPES = {
	'22': '"',
	'27': "'",
	'26': '&',
	'3c': '<',
	'3e': '>',
	'3C': '<',
	'3E': '>',
};

/**
 * Parse billing JSON that Blade put on a data- attribute.
 * Real module JS can use JSON.parse; CSP Alpine expressions cannot.
 */
export function parseAlpineJson(value, fallback = {}) {
	if (value == null || value === '') {
		return fallback;
	}

	if (typeof value === 'object') {
		return value;
	}

	if (typeof value !== 'string') {
		return fallback;
	}

	const candidates = [value, decodeHtmlEntities(value)];
	const decoded = candidates[1];

	if (decoded !== value) {
		candidates.push(restoreStrippedJsonHexEscapes(decoded));
	}

	candidates.push(restoreStrippedJsonHexEscapes(value));

	let lastError = null;

	for (const candidate of uniqueNonEmpty(candidates)) {
		try {
			return JSON.parse(candidate);
		} catch (error) {
			lastError = error;
		}
	}

	if (value.trim() !== '') {
		console.warn('Failed to parse billing details JSON', lastError);
	}

	return fallback;
}

export function loadBillingDetailsFromDom(el, fallback = {}) {
	const raw = el?.getAttribute?.(BILLING_DETAILS_ATTRIBUTE);

	if (raw == null || raw === '') {
		return fallback;
	}

	return parseAlpineJson(raw, fallback);
}

/**
 * Livewire's $wire magics override Alpine $el to the Livewire root.
 * Checkout and add-card put data-billing-details on a nested Alpine host,
 * so search the node, ancestors, and descendants.
 */
export function billingDetailsHost(component) {
	const nodes = uniqueElements([
		component?.$el,
		component?.$root,
	]);

	for (const node of nodes) {
		if (hasBillingDetailsAttribute(node)) {
			return node;
		}
	}

	for (const node of nodes) {
		if (typeof node.closest === 'function') {
			const ancestor = node.closest(`[${BILLING_DETAILS_ATTRIBUTE}]`);

			if (ancestor) {
				return ancestor;
			}
		}

		if (typeof node.querySelector === 'function') {
			const descendant = node.querySelector(`[${BILLING_DETAILS_ATTRIBUTE}]`);

			if (descendant) {
				return descendant;
			}
		}
	}

	if (typeof document !== 'undefined' && typeof document.querySelectorAll === 'function') {
		const matches = Array.from(document.querySelectorAll(`[${BILLING_DETAILS_ATTRIBUTE}]`));
		const visible = matches.find((node) => node.getClientRects?.().length > 0);

		return visible ?? matches[0] ?? null;
	}

	return null;
}

export function applyBillingDetailsFromDom(component) {
	const host = billingDetailsHost(component);
	const parsed = loadBillingDetailsFromDom(host);

	syncBillingDetails(component, parsed);
	syncBillingDetails(alpineDataFor(host), parsed);

	if (host) {
		hydrateBillingDetailsFromDom(host);
	}

	if (! parsed?.name && typeof component?.$nextTick === 'function' && ! component.__billingDetailsDomRetry) {
		component.__billingDetailsDomRetry = true;
		component.$nextTick(() => applyBillingDetailsFromDom(component));
	}

	return parsed;
}

/**
 * Livewire morph clones the parent _x_dataStack onto new nodes and skips nested
 * x-data. Alpine.$data then has no billingDetails even when $wire and the
 * attribute are correct. Prepend a reactive scope from module JS.
 */
let billingDetailsHydrationDepth = 0;
let billingDetailsObserver = null;

export function hydrateBillingDetailsFromDom(root = typeof document === 'undefined' ? null : document) {
	if (! root || billingDetailsHydrationDepth > 0) {
		return;
	}

	billingDetailsHydrationDepth += 1;

	try {
		hydrateBillingDetailsFromDomUnsafe(root);
	} finally {
		billingDetailsHydrationDepth -= 1;
	}
}

export function watchBillingDetailsHosts() {
	if (typeof document === 'undefined') {
		return;
	}

	hydrateBillingDetailsFromDom();

	if (billingDetailsObserver || typeof MutationObserver === 'undefined') {
		return;
	}

	billingDetailsObserver = new MutationObserver(() => {
		hydrateBillingDetailsFromDom();
	});

	const root = document.documentElement ?? document.body;

	if (root) {
		billingDetailsObserver.observe(root, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: [BILLING_DETAILS_ATTRIBUTE, 'x-data'],
		});
	}
}

function hydrateBillingDetailsFromDomUnsafe(root) {
	const hosts = [];

	if (hasBillingDetailsAttribute(root)) {
		hosts.push(root);
	}

	if (typeof root.querySelectorAll === 'function') {
		hosts.push(...root.querySelectorAll(`[${BILLING_DETAILS_ATTRIBUTE}]`));
	}

	for (const el of uniqueElements(hosts)) {
		prependSavedPayerScope(el);
	}
}

function prependSavedPayerScope(el) {
	const parsed = loadBillingDetailsFromDom(el);
	const alpine = alpineApi();
	const existing = alpineDataFor(el);
	const scope = savedPayerBilling(
		parsed?.name ?? '',
		parsed?.email ?? '',
		parsed?.address?.line1 ?? '',
		parsed?.address?.city ?? '',
		parsed?.address?.state ?? '',
		parsed?.address?.postal_code ?? '',
		parsed?.address?.country ?? '',
		existing?.paymentMethod ?? 'stripe',
	);

	scope.__savedPayerScope = true;

	const reactive = alpine?.reactive?.(scope) ?? scope;
	const rest = Array.isArray(el._x_dataStack)
		? el._x_dataStack.filter((item) => item !== reactive && item?.__savedPayerScope !== true)
		: (alpine?.closestDataStack?.(el) ?? []);

	el._x_dataStack = [reactive, ...rest];

	try {
		alpine?.addScopeToNode?.(el, reactive);
	} catch {
		// Livewire CSP Alpine still reads _x_dataStack even if addScopeToNode throws.
	}

	if (! Array.isArray(el._x_dataStack) || el._x_dataStack[0] !== reactive) {
		el._x_dataStack = [reactive, ...(Array.isArray(el._x_dataStack) ? el._x_dataStack : rest)];
	}

	syncBillingDetails(reactive, parsed);
	syncBillingDetails(el._x_dataStack[0], parsed);
	syncBillingDetails(alpineDataFor(el), parsed);

	try {
		el.dataset.billingHydrated = String(parsed?.name ?? '1');
	} catch {
		// dataset can throw on detached / XML nodes
	}
}

/**
 * Scalar-arg Alpine.data factory. CSP Alpine evaluates the call expression;
 * the returned nested billingDetails object never goes through the parser.
 */
export function savedPayerBilling(name, email, line1, city, state, postalCode, country, paymentMethod) {
	return {
		payerName: name ?? '',
		paymentMethod: paymentMethod ?? 'stripe',
		billingDetails: {
			name: name ?? '',
			email: email ?? '',
			address: {
				line1: line1 ?? '',
				city: city ?? '',
				state: state ?? '',
				postal_code: postalCode ?? '',
				country: country ?? '',
			},
		},
	};
}

/**
 * Blade calls checkoutPayment / invoicePaymentManager with positional @js() args.
 * Alpine.data wrappers pass one options object. A leading null (no Stripe secret yet)
 * must not be destructured as an object — that throws and skips init().
 */
export function paymentAlpineOptions(args, keys) {
	const first = args[0];

	if (first !== null && typeof first === 'object' && ! Array.isArray(first)) {
		return first;
	}

	const options = {};
	keys.forEach((key, index) => {
		options[key] = args[index] ?? null;
	});

	return options;
}

function hasBillingDetailsAttribute(node) {
	const raw = node?.getAttribute?.(BILLING_DETAILS_ATTRIBUTE);

	return raw != null && raw !== '';
}

function decodeHtmlEntities(value) {
	if (typeof document !== 'undefined' && typeof document.createElement === 'function') {
		const textarea = document.createElement('textarea');
		textarea.innerHTML = value;

		return textarea.value;
	}

	return value
		.replace(/&quot;/g, '"')
		.replace(/&#039;/g, "'")
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&amp;/g, '&');
}

/**
 * Livewire's CSP parser (and some HTML pipelines) drop the backslash from
 * JSON_HEX_* sequences such as \u0022. Restore those as real JSON punctuation.
 */
function restoreStrippedJsonHexEscapes(value) {
	return value.replace(/\\?u00(22|27|26|3[cCeE])/g, (match, hex) => HEX_JSON_ESCAPES[hex] ?? match);
}

function uniqueNonEmpty(values) {
	return [...new Set(values.filter((value) => typeof value === 'string' && value !== ''))];
}

function alpineApi() {
	return globalThis.Alpine ?? globalThis.window?.Alpine ?? null;
}

function alpineDataFor(el) {
	if (! el) {
		return null;
	}

	try {
		return alpineApi()?.$data?.(el) ?? null;
	} catch {
		return null;
	}
}

function syncBillingDetails(target, parsed) {
	if (! target || typeof target !== 'object') {
		return;
	}

	target.billingDetails = parsed;
	target.payerName = parsed.name ?? '';
}

function uniqueElements(nodes) {
	const seen = new Set();
	const result = [];

	for (const node of nodes) {
		if (! node || seen.has(node)) {
			continue;
		}

		seen.add(node);
		result.push(node);
	}

	return result;
}
